import { UseQueryOptions } from '@tanstack/react-query';
import {
	HubSpotInitiateOAuthPayload,
	HubSpotWebAppConnectionPayload,
	hubSpotConnectionControllerPath,
	hubSpotConnectionGetScopedConnectionsPath,
	hubSpotDisconnectConnectionPath,
	hubSpotOAuthInitiatePath,
	OAuthConnectionConfig,
} from 'ihub/common';
import { CRUDHelperBase } from '../classes/crud-helper-base';
import {
	buildURL,
	getTenantID,
	standardGet,
	standardPost,
	validateOkResponse,
} from '../classes/http-helper-const';
import Parameter from '../classes/query-parameter';
import { cacheStaleTime } from '../constants';
import QueryClientWrapper from '../utils/create-query-client';

export class HubSpotConnectionHttpService extends CRUDHelperBase<OAuthConnectionConfig> {
	constructor() {
		super(hubSpotConnectionControllerPath);
	}

	getScopedConnectionsQuery(
		queryParams?: Parameter[]
	): UseQueryOptions<OAuthConnectionConfig[], Error, OAuthConnectionConfig[]> {
		return {
			queryKey: [
				getTenantID(),
				this.objectType,
				hubSpotConnectionGetScopedConnectionsPath,
				queryParams?.map(x => `${x.key}=${x.value}`).join('&'),
			],
			queryFn: async (): Promise<OAuthConnectionConfig[]> => {
				const url = buildURL(
					this.objectType,
					[hubSpotConnectionGetScopedConnectionsPath],
					queryParams
				);
				const res = await standardGet(url);
				await validateOkResponse(res, 'get scoped connections', this.objectType);
				return await res.clone().json();
			},
			staleTime: cacheStaleTime,
		};
	}

	async initiateOAuth(payload: HubSpotInitiateOAuthPayload): Promise<{ consentUrl: string }> {
		const url = buildURL(this.objectType, [hubSpotOAuthInitiatePath]);
		const res = await standardPost(url, payload);
		await validateOkResponse(res, 'initiate OAuth', this.objectType);
		return await res.clone().json();
	}

	async disconnectConnection(payload: HubSpotWebAppConnectionPayload): Promise<boolean> {
		const url = buildURL(this.objectType, [hubSpotDisconnectConnectionPath]);
		const res = await standardPost(url, payload);
		await validateOkResponse(res, 'disconnect connection', this.objectType);
		return await res.clone().json();
	}

	async invalidateGetScopedConnections(remove = false) {
		await QueryClientWrapper.InvalidateKey(
			[getTenantID(), this.objectType, hubSpotConnectionGetScopedConnectionsPath],
			remove
		);
	}
}
